export type CapacityUnit = 'TiB' | 'GiB';

export const gibPerTib = 1024;

export interface CapacityBounds {
  min: number;
  max: number;
}

/**
 * Conservative fallback ceiling used while `useVolumeCapacityBounds` is in flight or when it
 * fails / returns no volumes. Kept small (1 TiB) so the placeholder slider range is narrow and
 * obviously temporary rather than masking as a real bound.
 *
 * Lives here, beside the rest of the capacity maths, rather than in `volumeFilters/constants`:
 * exploration needs it too, and that module's barrel pulls in React components that the
 * node-environment test suites cannot load.
 */
export const defaultMaxCapacityTib = 1;

/**
 * Fallback slider endpoints, used while `useVolumeCapacityBounds` is in flight, when it fails,
 * and by any surface with no volume to measure.
 */
export const defaultCapacityBounds: CapacityBounds = { min: 0, max: defaultMaxCapacityTib };

export function tibToUnit(tib: number, unit: CapacityUnit): number {
  return unit === 'GiB' ? tib * gibPerTib : tib;
}

export function unitToTib(val: number, unit: CapacityUnit): number {
  return unit === 'GiB' ? val / gibPerTib : val;
}

/** Display unit for a stored TiB value — TiB at or above 1 TiB, GiB below. */
export function defaultCapacityUnitForTib(tib: number): CapacityUnit {
  return tib >= 1 ? 'TiB' : 'GiB';
}

export interface ResolvedCapacityInput {
  unit: CapacityUnit;
  tib: number;
}

/** Maps a raw number-input value + unit to the stored TiB value, auto-switching to TiB above 1024 GiB. */
export function resolveCapacityInputValue(
  value: number,
  unit: CapacityUnit,
  fallbackTib: number
): ResolvedCapacityInput {
  let v = value;
  if (!Number.isFinite(v)) v = tibToUnit(fallbackTib, unit);

  if (unit === 'GiB' && v > gibPerTib) {
    return { unit: 'TiB', tib: unitToTib(v, 'GiB') };
  }

  return { unit, tib: unitToTib(v, unit) };
}

export function formatCapacityValue(tib: number, unit: CapacityUnit): string {
  const v = tibToUnit(tib, unit);
  return unit === 'GiB' ? String(Math.round(v)) : String(Number(v.toFixed(2)));
}

/**
 * Slider endpoints for a known largest volume, in TiB.
 *
 * The single place the ceiling is set, so every surface agrees. Callers convert into TiB
 * themselves — fleet and reports divide bytes by `bytesInTib`, exploration divides its GiB
 * `size` by 1024 — and pass their own `fallback` for "no volume to measure".
 *
 * The actual fractional TiB value is kept (no rounding up to a whole TiB) so that e.g. a
 * 1000 GiB volume shows as 1000 GiB rather than 1024 GiB. Floored at 1 GiB so the range is
 * never degenerate (`CapacityRangeFilter` renders "No capacity range available" when
 * `max <= min`).
 */
export function capacityBoundsFromMaxTib(
  maxTib: number | undefined,
  fallback: CapacityBounds = defaultCapacityBounds
): CapacityBounds {
  if (maxTib === undefined || !Number.isFinite(maxTib) || maxTib <= 0) return fallback;

  return { min: 0, max: Math.max(maxTib, 1 / gibPerTib) };
}

export function isCapacityRangeNarrowed(
  minTib: number,
  maxTib: number,
  bounds: CapacityBounds
): boolean {
  return minTib > bounds.min || maxTib < bounds.max;
}

/**
 * The range as a badge label, unconditionally.
 *
 * Split out of `formatCapacityRangeBadge` because the volume panel decides whether a capacity
 * clause is in play from `VolumeFilterPanelState.capacityFiltered`, not from narrowedness —
 * a range seeded from the URL can sit exactly on its (widened) bounds and still be a filter.
 */
export function formatCapacityRangeLabel(minTib: number, maxTib: number): string {
  const unit: CapacityUnit = minTib < 1 && maxTib < 1 ? 'GiB' : 'TiB';
  const lo = formatCapacityValue(minTib, unit);
  const hi = formatCapacityValue(maxTib, unit);
  return `${lo}-${hi} ${unit}`;
}

export function formatCapacityRangeBadge(
  minTib: number,
  maxTib: number,
  bounds: CapacityBounds
): string | undefined {
  if (!isCapacityRangeNarrowed(minTib, maxTib, bounds)) return undefined;

  return formatCapacityRangeLabel(minTib, maxTib);
}
